import type {
  PaymentsAmountCount,
  PaymentsDashboardData,
  PaymentsSeriePoint,
  PaymentsStatusSection,
} from '@app/domain/models/payments-dashboard/payments-dashboard.model';
import type {
  PaymentsDashboardAmountCountDto,
  PaymentsDashboardDto,
  PaymentsDashboardSerieRowDto,
} from '@app/data/dtos/payments/payments-dashboard.dto';

type Crudo = number | string | null | undefined;

function aNumero(v: Crudo): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/** Conteo: cualquier cosa que no sea un número (o string numérico) cuenta como 0 (R15). */
export function aConteo(v: Crudo): number {
  return aNumero(v) ?? 0;
}

/**
 * Monto: ausente → 0 (el back no mandó el campo porque no hubo nada); `null` o no
 * numérico → "sin dato". Distinguirlos es la regla de dinero: un `null` es un
 * `Sum` sobre filas sin monto, y pintarlo $0.00 diría que no se cobró (R15, R16).
 */
export function aMonto(v: Crudo): number | null {
  if (v === undefined) return 0;
  return aNumero(v);
}

function aConteoYMonto(conteo: Crudo, monto: Crudo): PaymentsAmountCount {
  return { count: aConteo(conteo), amount: aMonto(monto) };
}

function mapSerie(filas: PaymentsDashboardSerieRowDto[] | null | undefined): PaymentsSeriePoint[] {
  return (filas ?? []).reduce<PaymentsSeriePoint[]>((acc, fila) => {
    // Sin bucket no hay dónde dibujar el punto: se descarta.
    if (typeof fila?.month !== 'string' || fila.month.trim() === '') return acc;
    acc.push({
      bucket: fila.month,
      link: aConteoYMonto(fila.link_count, fila.link_amount),
      charge: aConteoYMonto(fila.charge_count, fila.charge_amount),
      qr: aConteoYMonto(fila.qr_count, fila.qr_amount),
    });
    return acc;
  }, []);
}

/** Copia moneda → estado → {conteo, monto} sin filtrar estados (filtrar es regla de presentación). */
function mapQr(
  qr: Record<string, Record<string, PaymentsDashboardAmountCountDto>> | null | undefined,
): PaymentsStatusSection {
  const resultado: Record<string, Record<string, PaymentsAmountCount>> = {};
  for (const [moneda, estados] of Object.entries(qr ?? {})) {
    if (!estados || typeof estados !== 'object') continue;
    const porEstado: Record<string, PaymentsAmountCount> = {};
    for (const [estado, bucket] of Object.entries(estados)) {
      porEstado[estado] = aConteoYMonto(bucket?.count, bucket?.amount);
    }
    resultado[moneda] = porEstado;
  }
  return resultado;
}

/** DTO del dashboard de pagos → entidad de dominio. */
export function mapPaymentsDashboard(dto: PaymentsDashboardDto | null | undefined): PaymentsDashboardData {
  return {
    periodLabel: dto?.period?.label ?? '',
    from: dto?.period?.from ?? '',
    to: dto?.period?.to ?? '',
    serie: mapSerie(dto?.serie),
    qr: mapQr(dto?.qr),
  };
}
